import { useState, useEffect, useCallback, useMemo } from 'react';
import { addressesApi } from '../services/addressApi';
import { Address, AddressSearchParams, CreateAddressRequest, UpdateAddressRequest } from '../types';
import { PaginatedResponse } from '../../../../types';
import { debounce } from '../../../../utils/validation';
import { useLoadingState } from '../../../../hooks/useLoadingState';

export const useAddresses = () => {
  const [addresses, setAddresses] = useState<Address[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { isLoading: globalLoading, message: loadingMessage, startLoading, stopLoading } = useLoadingState(250);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });

  const [searchParams, setSearchParams] = useState<AddressSearchParams>({
    search: '',
    page: 1,
    limit: 10,
    sorts: [{
      field: 'street1',
      direction: 'asc'
    }]
  });

  const fetchAddresses = useCallback(async (params: AddressSearchParams = {}) => {
    setLoading(true);
    setError(null);
    startLoading('fetch-addresses', 'Loading addresses...');
    
    try {
      const response: PaginatedResponse<Address> = await addressesApi.getAll(params);
      setAddresses(response.data);
      setPagination({
        page: response.page,
        limit: response.limit,
        total: response.total,
        totalPages: response.totalPages
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch addresses');
    } finally {
      setLoading(false);
      stopLoading('fetch-addresses');
    }
  }, [startLoading, stopLoading]);

  const debouncedFetchAddresses = useMemo(() => debounce(fetchAddresses, 300), [fetchAddresses]);

  useEffect(() => {
    fetchAddresses(searchParams);
  }, []);

  const updateSearchParams = useCallback((newParams: Partial<AddressSearchParams>) => {
    setSearchParams(prevParams => {
      const updatedParams = { ...prevParams, ...newParams };
      
      if (newParams.search !== undefined || newParams.fields !== undefined) {
        // Reset to first page when searching
        updatedParams.page = 1;
        debouncedFetchAddresses(updatedParams);
      } else if (newParams.sorts !== undefined) {
        // Reset to first page when sorting
        updatedParams.page = 1;
        fetchAddresses(updatedParams);
      } else if (newParams.page !== undefined || newParams.limit !== undefined) {
        // Handle page and limit changes immediately
        fetchAddresses(updatedParams);
      } else {
        fetchAddresses(updatedParams);
      }
      
      return updatedParams;
    });
  }, [debouncedFetchAddresses, fetchAddresses]);

  const createAddress = async (addressData: CreateAddressRequest) => {
    startLoading('create-address', 'Creating address...');
    try {
      setLoading(true);
      setError(null);
      const newAddress = await addressesApi.create(addressData);
      await fetchAddresses(); // Refresh the list
      return newAddress;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create address');
      throw err;
    } finally {
      setLoading(false);
      stopLoading('create-address');
    }
  };

  const updateAddress = async (id: number, addressData: UpdateAddressRequest) => {
    startLoading('update-address', 'Updating address...');
    try {
      setLoading(true);
      setError(null);
      const updatedAddress = await addressesApi.update(id, addressData);
      await fetchAddresses(); // Refresh the list
      return updatedAddress;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update address');
      throw err;
    } finally {
      setLoading(false);
      stopLoading('update-address');
    }
  };

  const deleteAddress = async (id: number) => {
    startLoading('delete-address', 'Deleting address...');
    try {
      await addressesApi.delete(id);
      await fetchAddresses(); // Refresh the list
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete address');
      return false;
    } finally {
      stopLoading('delete-address');
    }
  };

  return {
    addresses,
    loading,
    error,
    pagination,
    searchParams,
    updateSearchParams,
    createAddress,
    updateAddress,
    deleteAddress,
    refetch: fetchAddresses,
    globalLoading,
    loadingMessage
  };
};

